// Team-type buckets for the GM initiative picker panel (owner 2026-09-10).
//
// The panel used to split chips into "player characters" and "everything else", so a friendly
// summon (or a friendly NPC) rendered in the red row beside the enemies. Chips are grouped by the
// row's own team type instead (TeamType catalog: Friend / Enemy / Neutral), and within each bucket
// summons sit in their own sub-group below the main acting creatures — the same shape as the
// server's initiative order, which never rolls for a summon and appends every summon at the bottom
// (build_initiative_order / gather_summons). Pure: no DOM, no globals, so the contract test can run it.

import { TEAM_ENEMY, TEAM_FRIEND, TEAM_NEUTRAL } from './client-combat-membership';

export type CombatPickTeam = 'friend' | 'enemy' | 'neutral';

/** Bucket order in the panel: the GM's side, then the opposition, then bystanders. */
export const COMBAT_PICK_TEAM_ORDER: readonly CombatPickTeam[] = Object.freeze(['friend', 'enemy', 'neutral']);

export interface CombatPickBucketEntry {
    id: string;
    team: string;
    controllerId: string;
    isPlayer: boolean;
    name: string;
    pl: number;
}

export interface CombatPickBucket<T extends CombatPickBucketEntry> {
    team: CombatPickTeam;
    actors: T[];
    summons: T[];
}

/**
 * The row's team type. Unknown or absent teams follow the FT3D allegiance fallback
 * (storedActorAllegiance): a player character is friendly, anything else is neutral — never
 * hostile by default, matching opposingTeams(), where only Friend-vs-Enemy is opposition.
 */
export function combatPickTeam(team: string, isPlayer: boolean): CombatPickTeam {
    const id = team.trim().toUpperCase();
    if (id === TEAM_FRIEND) return 'friend';
    if (id === TEAM_ENEMY) return 'enemy';
    if (id === TEAM_NEUTRAL) return 'neutral';
    return isPlayer ? 'friend' : 'neutral';
}

/**
 * Group participants into team buckets (fixed COMBAT_PICK_TEAM_ORDER, empty buckets dropped).
 * Main actors: player characters by name, then everyone else by PL (highest first) and name.
 * Summons (any row with a controller) follow their controller's panel position, so each summon
 * lines up under whoever called it; a summon whose controller is not in the island sorts last.
 */
export function bucketCombatPick<T extends CombatPickBucketEntry>(entries: T[]): CombatPickBucket<T>[] {
    const buckets = new Map<CombatPickTeam, CombatPickBucket<T>>(
        COMBAT_PICK_TEAM_ORDER.map(team => [team, { team, actors: [], summons: [] }]),
    );
    for (const entry of entries) {
        const bucket = buckets.get(combatPickTeam(entry.team, entry.isPlayer))!;
        (entry.controllerId.trim() !== '' ? bucket.summons : bucket.actors).push(entry);
    }
    const byStrength = (a: T, b: T): number => (Number(b.isPlayer) - Number(a.isPlayer))
        || (a.isPlayer ? 0 : b.pl - a.pl)
        || a.name.localeCompare(b.name)
        || a.id.localeCompare(b.id);
    const ordered = [...buckets.values()].filter(bucket => bucket.actors.length + bucket.summons.length > 0);
    for (const bucket of ordered) bucket.actors.sort(byStrength);

    const position = new Map<string, number>();
    for (const bucket of ordered) {
        for (const actor of bucket.actors) position.set(actor.id.toUpperCase(), position.size);
    }
    // A summon's slot is its ROOT summoner's — a summon of a summon follows the chain up to the
    // main actor. A chain that leaves the island (or loops) sorts last.
    const entryById = new Map(entries.map(entry => [entry.id.toUpperCase(), entry]));
    const controllerSlot = (entry: T): number => {
        let current: T | undefined = entry;
        const seen = new Set<string>();
        while (current && current.controllerId.trim() !== '' && !seen.has(current.id)) {
            seen.add(current.id);
            current = entryById.get(current.controllerId.trim().toUpperCase());
        }
        return (current && current.controllerId.trim() === '' ? position.get(current.id.toUpperCase()) : undefined)
            ?? Number.MAX_SAFE_INTEGER;
    };
    for (const bucket of ordered) {
        bucket.summons.sort((a, b) => (controllerSlot(a) - controllerSlot(b)) || byStrength(a, b));
    }
    return ordered;
}
